import { matchPath } from 'react-router-dom'
import { ApprovalChecklist } from '../../../types/form-types'
import { WellMode } from '../../../types/routing-types'
import { Permissions, User } from '../../../types/user-types'
import { WorkflowStateInterface } from '../../components/wells/well-page'
import { WellState } from '../../context/well-context'
import { WellRecordStatus } from '../../services/constants'
import { isDriller, isCouncilUserOrAdmin, can } from '../../services/user-utils'
import { WorkflowNavLabels } from '../../../constants'
import { NavLinkProps } from '@msaf/core-react'

// Setup initial value to 1 so a check for falsy (0) is possible
export enum WorkflowNavStatus {
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_STARTED = 'NOT_STARTED',
}

export enum CompletedNavStep {
  NONE = 1,
  SETUP,
  DRILLING_INSTALLATION,
  REVIEW,
  COMPLETED,
}

export enum CompletedNavDrillingStep {
  NONE = 1,
  DETAILS,
  CONSTRUCTION,
  GEOTHERMAL,
  LITHOLOGY,
  WELLHEAD_SECURITY,
  DRINKING_WATER_SUPPLY,
  AQUIFER,
  YIELD_DRAWDOWN_TESTS,
  AQUIFER_TESTS,
  ADDITIONAL_DOCUMENTATION,
  SUBMISSION,
}
export interface CreateWellState {
  completedNavStep: CompletedNavStep
  completedDrillingStep: CompletedNavDrillingStep
  completedApprovalChecklistItems?: ApprovalChecklist
  id?: number | string
}

const EXCLUDED_DRILLER_LINKS: string[] = [WorkflowNavLabels.AQUIFER, WorkflowNavLabels.AQUIFER_TESTS]

const EXCLUDED_PUBLIC_USER_LINKS: string[] = [WorkflowNavLabels.EXTERNAL_DOCUMENT_REFERENCES]

const ONLY_COUNCIL_OR_ADMIN_LINKS: string[] = [WorkflowNavLabels.DRINKING_WATER_SUPPLY]

function maybeHideLinks(links: NavLinkProps[], user: User | null, mode: WellMode, wellMetaData: WellState) {
  const isDrillerUser = isDriller(user)
  const isCouncilOrAdminUser = isCouncilUserOrAdmin(user)
  const isPublicUser = !isDrillerUser && !isCouncilOrAdminUser
  const viewingPublishedWell = mode === WellMode.VIEW && wellMetaData?.isPublished

  return links.map((link) => {
    const summaryHidden = link.label === WorkflowNavLabels.SUMMARY && !viewingPublishedWell
    const geothermalHidden = link.label === WorkflowNavLabels.GEOTHERMAL && !wellMetaData?.isGeothermalWell
    const hiddenFromPublic = isPublicUser && EXCLUDED_PUBLIC_USER_LINKS.includes(link.label)
    const hiddenFromDriller = isDrillerUser && EXCLUDED_DRILLER_LINKS.includes(link.label)
    const onlyForCouncilOrAdmin = !isCouncilOrAdminUser && ONLY_COUNCIL_OR_ADMIN_LINKS.includes(link.label)

    link.isHidden = summaryHidden || geothermalHidden || hiddenFromPublic || hiddenFromDriller || onlyForCouncilOrAdmin

    return link
  })
}

export function getExistingWellLinks(
  user: User | null,
  id: string | number,
  mode: WellMode = WellMode.VIEW,
  wellMetaData: WellState,
) {
  const links: NavLinkProps[] = [
    { path: `/wells/${id}/${mode}/summary-details`, label: WorkflowNavLabels.SUMMARY },
    { path: `/wells/${id}/${mode}/details`, label: WorkflowNavLabels.DETAILS },
    { path: `/wells/${id}/${mode}/construction`, label: WorkflowNavLabels.CONSTRUCTION },
    { path: `/wells/${id}/${mode}/geothermal`, label: WorkflowNavLabels.GEOTHERMAL },
    { path: `/wells/${id}/${mode}/lithology`, label: WorkflowNavLabels.LITHOLOGY, exact: false },
    { path: `/wells/${id}/${mode}/wellhead-security`, label: WorkflowNavLabels.WELLHEAD_SECURITY },
    { path: `/wells/${id}/${mode}/drinking-water-supply`, label: WorkflowNavLabels.DRINKING_WATER_SUPPLY },
    { path: `/wells/${id}/${mode}/aquifer`, label: WorkflowNavLabels.AQUIFER },
    { path: `/wells/${id}/${mode}/drilling-tests`, label: WorkflowNavLabels.YIELD_DRAWDOWN_TESTS, exact: false },
    { path: `/wells/${id}/${mode}/aquifer-tests`, label: WorkflowNavLabels.AQUIFER_TESTS },
    { path: `/wells/${id}/${mode}/additional-documents`, label: WorkflowNavLabels.DOCUMENTS },
    { path: `/wells/${id}/view/external-document-references`, label: WorkflowNavLabels.EXTERNAL_DOCUMENT_REFERENCES },
  ]

  return maybeHideLinks(links, user, mode, wellMetaData)
}

export function getWellReviewLinks(id: string) {
  return [{ path: `/wells/${id}/edit/review`, label: WorkflowNavLabels.APPROVAL }]
}

interface NewWellLinks {
  id: string | number
  workflowState?: WorkflowStateInterface
  wellMetaData?: WellState
  user: User | null
  location?: string
  mode: WellMode
}

export function getNewWellLinks({ id, workflowState, wellMetaData = {}, user, location, mode }: NewWellLinks) {
  const wellSetupNavData = getNewWellSetupLinks(id, workflowState, location)
  const wellDrillingNavData = getNewWellDrillingLinks(user, id, mode, wellMetaData, workflowState)
  const wellReviewNavData = getNewWellReviewLinks(id, mode, workflowState)
  return [wellSetupNavData, wellDrillingNavData, wellReviewNavData]
}

function getNewWellSetupLinks(id?: string | number, workflowState?: WorkflowStateInterface, location?: string) {
  const isHidden =
    workflowState?.initialSetup?.details === WorkflowNavStatus.COMPLETED &&
    !(location && matchPath(location, id ? `/wells/${id}/new/setup` : '/wells/new/setup'))
  return {
    heading: '1. INITIAL SETUP',
    isSectionComplete: workflowState?.initialSetup?.stepStatus === WorkflowNavStatus.COMPLETED,
    links: !isHidden
      ? [
          {
            path: `/wells/${id}/edit/setup`,
            label: 'Well Creation',
            exact: false,
            checked: workflowState?.initialSetup?.details === WorkflowNavStatus.COMPLETED,
          },
        ]
      : [],
  }
}

function getNewWellDrillingLinks(
  user: User | null,
  id: string | number,
  mode: WellMode,
  workflowMetaData: WellState,
  workflowState?: WorkflowStateInterface,
) {
  const drillingNavData = {
    heading: '2. DRILLING & INSTALLATION',
    isSectionComplete: workflowState?.drillingAndInstallation?.stepStatus === WorkflowNavStatus.COMPLETED,
    adviceText: 'Complete initial setup',
    links: undefined,
  }

  if (workflowState?.initialSetup?.stepStatus === WorkflowNavStatus.COMPLETED) {
    return {
      ...drillingNavData,
      adviceText: undefined,
      links: getNewWellDrillingSectionLinks(user, id, mode, workflowMetaData, workflowState),
    }
  }

  return drillingNavData
}

function getNewWellDrillingSectionLinks(
  user: User | null,
  id: string | number,
  mode: WellMode,
  wellMetaData: WellState,
  workflowState?: WorkflowStateInterface,
) {
  const drillingLinks = [
    {
      path: `/wells/${id}/${mode}/details`,
      label: WorkflowNavLabels.DETAILS,
      checked: workflowState?.drillingAndInstallation?.details === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/construction`,
      label: WorkflowNavLabels.CONSTRUCTION,
      checked: workflowState?.drillingAndInstallation?.construction === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/geothermal`,
      label: WorkflowNavLabels.GEOTHERMAL,
      checked: workflowState?.drillingAndInstallation?.geothermal === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/lithology`,
      label: WorkflowNavLabels.LITHOLOGY,
      exact: false,
      checked: workflowState?.drillingAndInstallation?.lithology === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/wellhead-security`,
      label: WorkflowNavLabels.WELLHEAD_SECURITY,
      checked: workflowState?.drillingAndInstallation?.wellheadSecurity === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/drinking-water-supply`,
      label: WorkflowNavLabels.DRINKING_WATER_SUPPLY,
      checked: workflowState?.drillingAndInstallation?.drinkingWater === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/aquifer`,
      label: WorkflowNavLabels.AQUIFER,
      checked: workflowState?.drillingAndInstallation?.aquifer === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/drilling-tests`,
      label: WorkflowNavLabels.YIELD_DRAWDOWN_TESTS,
      exact: false,
      checked: workflowState?.drillingAndInstallation?.drillingTests === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/aquifer-tests`,
      label: WorkflowNavLabels.AQUIFER_TESTS,
      exact: false,
      checked: workflowState?.drillingAndInstallation?.aquiferTests === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/additional-documents`,
      label: WorkflowNavLabels.DOCUMENTS,
      checked: workflowState?.drillingAndInstallation?.additionalDocuments === WorkflowNavStatus.COMPLETED,
    },
    {
      path: `/wells/${id}/${mode}/submission`,
      label: WorkflowNavLabels.SUBMISSION,
      checked: workflowState?.drillingAndInstallation?.submission === WorkflowNavStatus.COMPLETED,
    },
  ]

  return maybeHideLinks(drillingLinks, user, mode, wellMetaData)
}

function getNewWellReviewLinks(id: string | number, mode: WellMode, workflowState?: WorkflowStateInterface) {
  const baseReviewNavData = {
    heading: '3. REVIEW',
  }

  // If drilling is complete show links for Review section
  if (workflowState?.drillingAndInstallation?.stepStatus === WorkflowNavStatus.COMPLETED) {
    return {
      ...baseReviewNavData,
      links: [{ path: `/wells/${id}/${mode}/approve`, label: WorkflowNavLabels.APPROVAL, checked: false }],
    }
  }

  // All sections are completed
  if (workflowState?.review?.stepStatus === WorkflowNavStatus.COMPLETED) {
    return {
      ...baseReviewNavData,
      isSectionComplete: true,
      adviceText: '',
    }
  }

  // Default to show Show heading and advice text
  return {
    ...baseReviewNavData,
    adviceText: 'Complete drilling & installation',
  }
}

export const getRouteForWellStatus = (user: User | null, id: number, wellRecordStatus: string) => {
  // Draft well - go to Well Creation Workflow
  if (
    can(user, [Permissions.CREATE_WELL, Permissions.CREATE_WELL_UNSCOPED]) &&
    wellRecordStatus === WellRecordStatus.DRAFT
  ) {
    return `/wells/${id}/edit/details`
    // If not published go to details
  } else if (can(user, [Permissions.VIEW_WELL]) && wellRecordStatus !== WellRecordStatus.PUBLISHED) {
    return `/wells/${id}/view/details`
  } else {
    // Else go to summary details
    return `/wells/${id}/view/summary-details`
  }
}
